import { useEffect, useState } from 'react'

import { Box, Container } from '@mui/material'
import { GAME_STEP } from 'constants/enums/GameStepEnum'
import { GAME_TYPE } from 'constants/enums/GameTypeEnum'
import { ToastSeverity } from 'constants/enums/ToastSeverityEnum'
import { BusinessTopic } from 'constants/enums/TopicTypeEnum'
import { SUBMITTED_IDEAS } from 'constants/routes'
import { alertTitleTextMap, defaultErrorMessage } from 'content/alertText'
import { hasUnsavedIdeation } from 'utils/ideationUtils'
import logger from 'utils/logger'
import {
	guardUnsavedAction,
	useUnsavedIdeationWarning,
} from 'utils/useUnsavedIdeationWarning'

import { TeeterTotterModel } from 'models/TeeterTotterModel'
import { Game } from 'models/gameModels'
import { TeeterTotterPrompt } from 'models/ideaModels'

import { useIdeaStreakContext } from 'contexts/IdeaStreakProvider'
import { useModalContext } from 'contexts/ModalProvider'
import { useToastContext } from 'contexts/ToastProvider'

import { postTeeterTotterResponse } from 'services/completions.service'
import { getTeeterTotterRandomPrompts, postIdea } from 'services/ideas.service'

import CustomGameTitleBar from 'components/CustomGameTitleBar/CustomGameTitleBar'
import Hero from 'components/Hero'
import { IdeationRecommendationModal } from 'components/IdeationRecommendationModal/IdeationRecommendationModal'

import Section1 from './components/Section1'
import Section2 from './components/Section2'
import Section3 from './components/Section3'

export const TEST_ID = 'teeter-totter'

const TeeterTotter = ({ game }: { game?: Game }) => {
	const { setIdeaStreakCount } = useIdeaStreakContext()

	const [refreshClicksSinceIdeation, setRefreshClicksSinceIdeation] =
		useState(0)

	const {
		authorIdeation,
		centerCategory,
		companyProblem,
		gameToJSON,
		generatePromptsDisabled,
		submitIdeasDisabled,
		setGameId,
		termsAgreed,
		setTermsAgreed,
		innovationCompany,
		innovationTopic,
		leftExtreme,
		reset,
		rightExtreme,
		setAuthorIdeation,
		setCenterCategory,
		setCompanyProblem,
		setInnovationCompany,
		setInnovationTopic,
		setLeftExtreme,
		setRightExtreme,
		getRandomPromptsQueryObj,
		setPreviousPrompts,
		setPromptResponse,
		resetSoft,
		responseToJSON,
		responseConversation,
		setResponseConversation,
		setCurrentResponses,
		currentResponses,
		currentPrompts,
		setCurrentPrompts,
	} = TeeterTotterModel()

	const [isPromptLoading, setIsPromptLoading] = useState(false)
	const [isSubmitButtonLoading, setIsSubmitButtonLoading] = useState(false)
	const { showAlert } = useToastContext()
	const [step, setStep] = useState<GAME_STEP>(GAME_STEP.Step1)
	const [isAiLoading, setIsAiLoading] = useState(false)
	const [aiExampleVisible, setAiExampleVisible] = useState<boolean>(false)

	const userMessage = {
		content: '',
		role: 'user',
	}

	const updateAIResponse = (content: string) => {
		return {
			content,
			role: 'assistant',
		}
	}

	useEffect(() => {
		if (game) {
			setInnovationCompany(game.innovationCompany)
			setCompanyProblem(game.companyProblem)
			setInnovationTopic(
				BusinessTopic[
					game.innovationTopic as keyof typeof BusinessTopic
				]
			)
			setGameId(game.gameId)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [game])

	const { showModal } = useModalContext()
	const isUnsaved = hasUnsavedIdeation(step, authorIdeation)

	useUnsavedIdeationWarning(isUnsaved, authorIdeation, showModal)

	const handleManualPromptChange = async (newValues: TeeterTotterPrompt) => {
		return guardUnsavedAction({
			isUnsaved,
			showModal,
			onProceed: async () => {
				try {
					setCurrentPrompts(newValues)

					await handleAiResponseClick(newValues, false, true)
				} catch (error) {
					if (showAlert) {
						showAlert(defaultErrorMessage)
					}
				} finally {
					setIsPromptLoading(false)
				}
			},
		})
	}

	const handleAiResponseClick = async (
		additionalProps: TeeterTotterPrompt,
		isUserInitiated = true,
		resetConversation: boolean
	) => {
		if (isUserInitiated && !aiExampleVisible) {
			setAiExampleVisible(true)
			return
		}

		setIsAiLoading(true)
		try {
			const prompt = responseToJSON()
			prompt.conversation = [...responseConversation, userMessage]
			if (additionalProps.promptId) {
				prompt.prompt = additionalProps
			}
			const { status, data } = await postTeeterTotterResponse(prompt)

			if (status === 200 && data) {
				const msg = JSON.stringify(data)
				if (resetConversation) {
					setResponseConversation([])
				} else {
					const updatedConversation = [
						...responseConversation,
						userMessage,
						updateAIResponse(msg),
					]

					setResponseConversation(updatedConversation)
				}
				setCurrentResponses(data)
			} else {
				throw new Error(`Unexpected response: ${JSON.stringify(data)}`)
			}
		} catch (error) {
			logger.error('Error in AI response handling:', error)
			if (showAlert) {
				showAlert(defaultErrorMessage)
			}
		} finally {
			setIsAiLoading(false)
		}
	}

	const refreshPrompt = async () => {
		return guardUnsavedAction({
			isUnsaved,
			showModal,
			onProceed: async () => {
				if (refreshClicksSinceIdeation < 2) {
					await handleRandomPrompts()
				}

				setRefreshClicksSinceIdeation((prev) => prev + 1)
			},
		})
	}

	const handleRandomPrompts = async () => {
		try {
			resetSoft()
			setAiExampleVisible(false)
			setIsPromptLoading(true)
			setStep(GAME_STEP.Step2)

			const { status, data } = await getTeeterTotterRandomPrompts(
				getRandomPromptsQueryObj()
			)

			if (status === 200 && data.prompt) {
				const { prompt } = data

				setLeftExtreme(prompt.leftConstraint)
				setCenterCategory(prompt.topic)
				setRightExtreme(prompt.rightConstraint)

				setIsAiLoading(true)
				handleAiResponseClick(prompt, false, true)

				if (data.resetPreviousPrompts) {
					setPreviousPrompts([])
				}
				setPromptResponse(prompt)
			} else {
				throw new Error(`Unexpected response: ${JSON.stringify(data)}`)
			}
		} catch (error) {
			logger.error('Error fetching random prompts:', error)
			if (showAlert) {
				showAlert(defaultErrorMessage)
			}
		} finally {
			setIsPromptLoading(false)
		}
	}

	const resetAllField = () => {
		setInnovationCompany('')
		setInnovationTopic('')
		setCompanyProblem('')
	}

	const handleSubmitIdeas = async () => {
		setIsSubmitButtonLoading(true)

		try {
			const response = await postIdea(gameToJSON())
			if (response.status !== 200) {
				throw new Error(
					`Failed to submit idea. Status: ${response.status}`
				)
			}

			showAlert?.({
				severity: ToastSeverity.SUCCESS,
			})
			setIdeaStreakCount((prevState) => prevState + 1)
			setRefreshClicksSinceIdeation(refreshClicksSinceIdeation + 1)
			reset()

			window.scrollTo({
				top: 0,
			})
			handleRandomPrompts()
		} catch (error) {
			logger.error(error)
			if (showAlert) {
				showAlert({
					title: alertTitleTextMap.submitIdeaError.title,
					bodyText: alertTitleTextMap.submitIdeaError.text,
					severity: ToastSeverity.ERROR,
				})
			}
		} finally {
			setIsSubmitButtonLoading(false)
		}
	}

	const onIdeationRecommendationBackClicked = () => {
		setStep(GAME_STEP.Step1)
		onIdeationRecommendationModalClosed()
	}

	const onIdeationRecommendationModalClosed = () => {
		setRefreshClicksSinceIdeation(0)
	}

	const viewSubmittedIdeas = () => {
		window.open(`${window.location.origin}${SUBMITTED_IDEAS}`, '_blank')
	}

	useEffect(() => {
		setRefreshClicksSinceIdeation(0)
	}, [authorIdeation])

	return (
		<Box>
			<IdeationRecommendationModal
				refreshClicksSinceIdeation={refreshClicksSinceIdeation}
				onBack={onIdeationRecommendationBackClicked}
				onClose={onIdeationRecommendationModalClosed}
			/>
			<Hero
				step={step}
				gameTypeEnum={GAME_TYPE.TEETER_TOTTER}
				innovationCompany={innovationCompany}
				setInnovationCompany={setInnovationCompany}
				innovationTopic={innovationTopic}
				setInnovationTopic={setInnovationTopic}
				companyProblem={companyProblem}
				setCompanyProblem={setCompanyProblem}
				isLoading={isPromptLoading}
				createPrompt={handleRandomPrompts}
			/>
			<Container
				data-testid={`${TEST_ID}-container`}
				maxWidth={step === GAME_STEP.Step1 ? 'md' : 'lg'}
				sx={{
					paddingTop: '3rem',
					paddingBottom: '3rem',
					paddingX: '2rem',
				}}
			>
				{game && (game.gameTitle || game.endTime) && (
					<CustomGameTitleBar game={game} />
				)}
				{step === GAME_STEP.Step1 && (
					<Section1
						isLoading={isPromptLoading}
						innovationCompany={innovationCompany}
						setInnovationCompany={setInnovationCompany}
						companyProblem={companyProblem}
						setCompanyProblem={setCompanyProblem}
						resetAllField={resetAllField}
						createPrompt={handleRandomPrompts}
						generatePromptsDisabled={generatePromptsDisabled}
						innovationTopic={innovationTopic}
						setInnovationTopic={setInnovationTopic}
						editable={
							game
								? game.gameTitle === '' && game.endTime === ''
								: true
						}
					/>
				)}
				{step === GAME_STEP.Step2 && (
					<>
						<Section2
							isLoading={isPromptLoading}
							onRefreshPromptClick={refreshPrompt}
							onEditPromptClick={() => {}}
							leftCardValue={leftExtreme}
							centerCardValue={centerCategory}
							rightCardValue={rightExtreme}
							currentResponses={currentResponses}
							isAiLoading={isAiLoading}
							teeterTotterPrompt={currentPrompts}
							changePrompts={handleManualPromptChange}
						/>
						<Section3
							isLoading={isSubmitButtonLoading}
							authorIdeation={authorIdeation}
							setAuthorIdeation={setAuthorIdeation}
							onSubmitIdeaClick={handleSubmitIdeas}
							onViewIdeasClick={viewSubmittedIdeas}
							termsAgreed={termsAgreed}
							setTermsAgreed={setTermsAgreed}
							gameId={game?.gameId}
							submitIdeasDisabled={submitIdeasDisabled}
						/>
					</>
				)}
			</Container>
		</Box>
	)
}

export default TeeterTotter
